import { Box, Card, CardContent, Container } from '@mui/material'
import { Loader, useMinimalsTheme } from '@tom-ui/ui'
import { appStore, PATHS, useAsyncFetch, withAuthenticationRequired } from '@tom-ui/utils'
import { ImoClassAutoCompleteProps } from '@tom-ui/planning'
import { AllocationRuleForm } from '@yard/components/AllocationRules/AllocationRuleForm'
import { AllocationRuleFormStore } from '@yard/components/AllocationRules/stores/allocation-rule.form-store'
import { observer } from 'mobx-react-lite'
import { FC, ReactNode, useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useYardStores } from '../../AppProvider'
import { AllocationRuleTemplateFormHeader } from './components/molecules/AllocationRuleTemplateFormHeader'
import { useAllocationRuleTemplateMessageHandler } from './hooks/useAllocationRuleTemplateMessageHandler'
import { AllocationRulesTemplateViewStore } from './stores/allocation-rules-template.view-store'

type Props = {
  companyAutoComplete: (props: any) => ReactNode
  locationCodeAutoComplete: (props: any) => ReactNode
  imoClassAutoComplete: (props: ImoClassAutoCompleteProps) => ReactNode
}

export const AllocationRuleTemplateFormPage: FC<Props> = withAuthenticationRequired(
  observer(props => {
    const { companyAutoComplete, locationCodeAutoComplete, imoClassAutoComplete } = props

    const { id } = useParams<{ id: string }>()
    const theme = useMinimalsTheme()
    const navigate = useNavigate()
    const { getSuccessMessage, getExceptionMessage } = useAllocationRuleTemplateMessageHandler()

    const { allocationRuleStore, yardBlockStore } = useYardStores()

    const { loading } = useAsyncFetch(async () => {
      await Promise.all([allocationRuleStore.loadTemplateRules(), yardBlockStore.loadYardBlocks()])
    }, [allocationRuleStore, yardBlockStore])

    const formStore = useMemo(() => new AllocationRuleFormStore(), [])

    const allocationRulesTemplateViewStore = useMemo(() => {
      const store = new AllocationRulesTemplateViewStore(
        allocationRuleStore,
        companyAutoComplete,
        locationCodeAutoComplete,
        imoClassAutoComplete,
        id,
      )

      return store
    }, [
      id,
      companyAutoComplete,
      locationCodeAutoComplete,
      imoClassAutoComplete,
      allocationRuleStore,
    ])

    useEffect(() => {
      if (
        allocationRulesTemplateViewStore.isEdit &&
        allocationRulesTemplateViewStore.allocationRuleTemplate
      ) {
        formStore.setValues(allocationRulesTemplateViewStore.allocationRuleTemplate)
      }
    }, [
      allocationRulesTemplateViewStore.allocationRuleTemplate,
      allocationRulesTemplateViewStore.isEdit,
      formStore,
    ])

    const onSubmit = async () => {
      try {
        if (formStore.validate()) {
          await allocationRuleStore.upsertTemplateAllocationRuleByAllocationRuleFormData(
            formStore.data,
            allocationRulesTemplateViewStore.isEdit
              ? allocationRulesTemplateViewStore.allocationRuleTemplateId!
              : undefined,
          )

          appStore.setShowAlert(
            'success',
            getSuccessMessage(allocationRulesTemplateViewStore.isEdit),
          )
          navigate(PATHS.masterData.allocationRuleTemplates.url)
        }
      } catch (error) {
        appStore.setShowAlert('error', getExceptionMessage(error))
      }
    }

    if (loading) return <Loader show />

    return (
      <Box sx={{ bgcolor: theme.palette.grey[100], minHeight: '100%' }}>
        <AllocationRuleTemplateFormHeader
          isEdit={allocationRulesTemplateViewStore.isEdit}
          onSubmit={onSubmit}
        />
        <Container sx={{ paddingY: 2 }}>
          <Card>
            <CardContent>
              <AllocationRuleForm
                formStore={formStore}
                companyAutoComplete={allocationRulesTemplateViewStore.companyAutoComplete}
                locationCodeAutoComplete={allocationRulesTemplateViewStore.locationCodeAutoComplete}
                imoClassAutoComplete={allocationRulesTemplateViewStore.imoClassAutoComplete}
                hideCarrierFields
              />
            </CardContent>
          </Card>
        </Container>
      </Box>
    )
  }),
)
